/**
 * Transfer In Form — review/edit a pending Transfer In at the destination location.
 * Shows the issued quantity (what left the source, read-only reference) next to an
 * editable received quantity per line, defaulted to a full receipt but clampable down
 * to record a shortage. "Save" persists the entered quantities without finalizing
 * anything; "Approve" finalizes and is what actually credits destination stock.
 */

import React, { useState } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import toast from 'react-hot-toast';

const TransferInForm = ({ transferIn, onClose, onSuccess, onApprove }) => {
  const { updateTransferIn } = useInventory();

  const [lines, setLines] = useState(() => {
    const initial = {};
    (transferIn.lineItems || []).forEach(line => {
      initial[line.ginLineItemId] = {
        quantityReceived: line.quantityReceived ?? line.quantityIssued,
        condition: line.condition || 'good',
        notes: line.notes || ''
      };
    });
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleFieldChange = (key, field, value) => {
    setLines(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    if (field === 'quantityReceived' && errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    (transferIn.lineItems || []).forEach(line => {
      const key = line.ginLineItemId;
      const qty = parseFloat(lines[key]?.quantityReceived);
      if (isNaN(qty) || qty < 0) {
        newErrors[key] = 'Required';
      } else if (qty > line.quantityIssued) {
        newErrors[key] = `Max ${line.quantityIssued}`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    lineItems: (transferIn.lineItems || []).map(line => ({
      ginLineItemId: line.ginLineItemId,
      quantityReceived: parseFloat(lines[line.ginLineItemId]?.quantityReceived),
      condition: lines[line.ginLineItemId]?.condition,
      notes: lines[line.ginLineItemId]?.notes
    }))
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await updateTransferIn(transferIn._id, buildPayload());
      toast.success('Transfer-in updated');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update transfer-in';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveClick = () => {
    if (!validate()) return;
    onApprove && onApprove(transferIn, buildPayload());
  };

  const inputCls = (hasError) =>
    `w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-[60]">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  Receive Transfer — {transferIn.transferInNo}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSave} className="px-4 pt-3 pb-2">
            {submitError && (
              <div className="mb-2 bg-red-50 border border-red-200 rounded p-2">
                <p className="text-xs text-red-700">{submitError}</p>
              </div>
            )}

            <div className="mb-2 bg-blue-50 border border-blue-200 rounded p-2">
              <p className="text-xs text-blue-700">
                Enter the actual quantity received per line. Approving credits your warehouse's stock for the received amount only — any shortfall is recorded, not silently dropped.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-500">From: </span>
                {transferIn.sourceWarehouseName} ({transferIn.sourceLocationName})
              </div>
              <div>
                <span className="font-medium text-gray-500">GTR: </span>
                {transferIn.gtrRequestNo}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
              <div className="bg-gray-50 px-3 py-1.5">
                <span className="text-xs font-semibold text-gray-700">Line Items</span>
              </div>
              <div className="max-h-[45vh] overflow-y-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">
                        {transferIn.procurementType === 'FIXED_ASSET' ? 'Fixed Asset' : 'Item'}
                      </th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Issued</th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Received Qty *</th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Short</th>
                      <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Condition</th>
                      <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {(transferIn.lineItems || []).map(line => {
                      const key = line.ginLineItemId;
                      const received = parseFloat(lines[key]?.quantityReceived);
                      const short = !isNaN(received) ? Math.max(0, line.quantityIssued - received) : 0;
                      return (
                        <tr key={key}>
                          <td className="px-2 py-1.5">
                            <div className="text-xs font-medium text-gray-900">{line.itemDescription || line.assetName}</div>
                            <div className="text-xs text-gray-500 font-mono">{line.itemCode || line.assetCode}</div>
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityIssued}</td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="0"
                              max={line.quantityIssued}
                              step="0.001"
                              value={lines[key]?.quantityReceived ?? ''}
                              onChange={(e) => handleFieldChange(key, 'quantityReceived', e.target.value)}
                              className={`${inputCls(!!errors[key])} w-24 text-center mx-auto`}
                            />
                            {errors[key] && (
                              <p className="text-[10px] text-red-500 mt-0.5 text-center">{errors[key]}</p>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {short > 0 ? (
                              <span className="text-xs font-medium text-red-600">{short}</span>
                            ) : (
                              <span className="text-xs text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={lines[key]?.condition || 'good'}
                              onChange={(e) => handleFieldChange(key, 'condition', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="good">Good</option>
                              <option value="damaged">Damaged</option>
                              <option value="lost">Lost</option>
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={lines[key]?.notes || ''}
                              onChange={(e) => handleFieldChange(key, 'notes', e.target.value)}
                              placeholder="Optional"
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleApproveClick}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Approve & Receive
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferInForm;
